import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { EyeOff, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { EmptyState } from "@/components/empty-state";
import { StatusBadge } from "@/components/studio/status-badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { getMyPublications } from "@/lib/writer.functions";

export const Route = createFileRoute("/_authenticated/studio/publications")({
  head: () => ({
    meta: [
      { title: "Your publications — Creator Studio — Quibblies" },
      {
        name: "description",
        content:
          "Every publication you've written on Quibblies, grouped by published, drafts, under review, changes requested and rejected.",
      },
      { property: "og:title", content: "Your publications — Quibblies Creator Studio" },
      { property: "og:description", content: "Track every publication and its review status." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: PublicationsPage,
});

const GROUPS = [
  { key: "published", label: "Published", statuses: ["published"] },
  { key: "drafts", label: "Drafts", statuses: ["draft"] },
  { key: "review", label: "Under review", statuses: ["submitted", "under_review"] },
  { key: "changes", label: "Changes requested", statuses: ["changes_requested"] },
  { key: "rejected", label: "Rejected", statuses: ["rejected"] },
  { key: "scheduled", label: "Approved / scheduled", statuses: ["approved"] },
] as const;

export function PublicationsList({ only }: { only?: readonly string[] }) {
  const queryClient = useQueryClient();
  const fetchPublications = useServerFn(getMyPublications);
  const { data, isLoading } = useQuery({
    queryKey: ["studio-publications"],
    queryFn: () => fetchPublications(),
  });

  const unpublish = useMutation({
    mutationFn: async (storyId: string) => {
      const { error } = await supabase
        .from("stories")
        .update({ status: "draft", published_at: null })
        .eq("id", storyId)
        .eq("status", "published");
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["studio-publications"] });
      void queryClient.invalidateQueries({ queryKey: ["my-stories"] });
      toast.success("Publication unpublished and returned to drafts");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const deleteDraft = useMutation({
    mutationFn: async (storyId: string) => {
      const { error } = await supabase
        .from("stories")
        .delete()
        .eq("id", storyId)
        .eq("status", "draft");
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["studio-publications"] });
      void queryClient.invalidateQueries({ queryKey: ["my-stories"] });
      toast.success("Draft permanently deleted");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, index) => (
          <Skeleton key={index} className="h-24 w-full rounded-2xl" />
        ))}
      </div>
    );
  }

  const rows = (data ?? []).filter((row) => !only || only.includes(row.status));
  const groups = GROUPS.filter((group) => !only || group.statuses.some((s) => only.includes(s)));

  if (rows.length === 0) {
    return (
      <EmptyState
        title="Nothing here yet"
        description="Start a new publication — drafts stay private until you submit them for review."
        action={
          <Button asChild variant="hero" size="pill">
            <Link to="/write">Create publication</Link>
          </Button>
        }
      />
    );
  }

  return (
    <div className="space-y-8">
      {groups.map((group) => {
        const items = rows.filter((row) => group.statuses.includes(row.status as never));
        if (items.length === 0) return null;
        return (
          <section key={group.key}>
            <h2 className="font-display text-lg text-foreground">
              {group.label}{" "}
              <span className="text-sm text-muted-foreground">({items.length})</span>
            </h2>
            <ul className="mt-3 space-y-3">
              {items.map((item) => (
                <li
                  key={item.id}
                  className="rounded-2xl border border-border/60 bg-background/60 p-4"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-display text-lg text-foreground">{item.title}</p>
                      <p className="mt-1 text-sm text-muted-foreground line-clamp-2">
                        {item.description ?? "No description yet."}
                      </p>
                      <p className="mt-2 text-xs text-muted-foreground">
                        {item.content_types?.name ?? "Uncategorised"} ·{" "}
                        {item.word_count.toLocaleString()} words
                        {item.pen_names?.name ? ` · as ${item.pen_names.name}` : ""}
                      </p>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-2">
                      <StatusBadge status={item.status} />
                      {item.status === "published" ? (
                        <div className="flex flex-wrap justify-end gap-1">
                          <Button asChild variant="ghost" size="sm">
                            <Link to="/story/$storyId" params={{ storyId: item.id }}>View</Link>
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="sm"><EyeOff aria-hidden="true" /> Unpublish</Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Unpublish this publication?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  It will disappear from readers immediately and return to your private drafts. Its likes and comments are kept.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Keep published</AlertDialogCancel>
                                <AlertDialogAction onClick={() => unpublish.mutate(item.id)}>Unpublish</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      ) : (
                        <div className="flex flex-wrap justify-end gap-1">
                          <Button asChild variant="ghost" size="sm"><Link to="/write">Edit</Link></Button>
                          {item.status === "draft" ? (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="sm"><Trash2 aria-hidden="true" /> Delete</Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete this draft permanently?</AlertDialogTitle>
                                  <AlertDialogDescription>This cannot be undone. The draft and its chapters will be removed.</AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    onClick={() => deleteDraft.mutate(item.id)}
                                  >Delete draft</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          ) : null}
                        </div>
                      )}
                    </div>
                  </div>
                  {item.status === "published" ? (
                    <p className="mt-3 text-xs text-muted-foreground">
                      {item.view_count.toLocaleString()} views · {item.like_count} likes ·{" "}
                      {item.bookmark_count} bookmarks · {item.comment_count} comments
                    </p>
                  ) : null}
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

function PublicationsPage() {
  return <PublicationsList />;
}
